import { Navigate, Route, Routes } from 'react-router-dom';
import Shell from './components/Shell';
import { BetaAgreementWatcher, RequireAuth, RequireLevel, RequireRole } from './components/guards';
import { useSession } from './lib/session';
import Approvals from './pages/Approvals';
import BetaAgreement from './pages/BetaAgreement';
import Files from './pages/Files';
import JobDetail from './pages/JobDetail';
import Jobs from './pages/Jobs';
import MobileRequired from './pages/MobileRequired';
import NewJob from './pages/NewJob';
import OfficeSetup from './pages/OfficeSetup';
import Overview from './pages/Overview';
import Properties from './pages/Properties';
import Settings from './pages/Settings';
import SignIn from './pages/SignIn';
import Verify from './pages/Verify';
import EarlyAccess from './pages/EarlyAccess';
import AdminEarlyAccess from './pages/AdminEarlyAccess';
import Team from './pages/Team';
import Wallet from './pages/Wallet';
import DoorShell from './parcels/DoorShell';
import ParcelDoor from './parcels/ParcelDoor';
import ParcelHub, { ParcelResolver } from './parcels/ParcelHub';

/**
 * Role-aware default view (Run 5): homeowners land on their properties,
 * contractors keep the jobs overview. One dashboard, two doors.
 */
function RoleHome() {
  const { contractor } = useSession();
  // Run 10: a brokerage field seat is the listing agent — home is their files.
  if (contractor?.companyKind === 'brokerage' && contractor.permissionLevel === 'field') {
    return <Navigate to="/files" replace />;
  }
  return <Navigate to={contractor?.accountRole === 'homeowner' ? '/properties' : '/dashboard'} replace />;
}

export default function App() {
  return (
    <>
      <BetaAgreementWatcher />
      <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      {/* Public: document verification by printed ID. No session needed. */}
      <Route path="/verify" element={<Verify />} />
      <Route path="/verify/:id" element={<Verify />} />
      {/* Public: early-access request form (linked from posts with ?src=). */}
      <Route path="/early-access" element={<EarlyAccess />} />
      <Route path="/signup" element={<SignIn />} />
      {/* Outside RequireAuth: a field user is signed in, just not welcome here. */}
      <Route path="/mobile-required" element={<MobileRequired />} />

      {/* Run 11 early access wall. Signed in but not yet accepted, so it is the
          one authenticated route that must stay open to an un-accepted seat —
          and it sits outside Shell, because none of that navigation works yet. */}
      <Route
        path="/beta-agreement"
        element={
          <RequireAuth allowUnaccepted>
            <BetaAgreement />
          </RequireAuth>
        }
      />

      {/* Run 6 public doors: the seat URL is the credential, so no RequireAuth here.
          The door itself decides between capability token, bearer session, and the file wall. */}
      <Route element={<DoorShell />}>
        <Route path="/parcels/:id" element={<ParcelResolver />} />
        <Route path="/parcels/:id/:seat" element={<ParcelDoor />} />
        <Route path="/parcels/:id/:seat/join" element={<ParcelDoor join />} />
      </Route>

      {/* The membership list and CS view-as remain bearer surfaces. */}
      <Route element={<RequireAuth><Shell parcelOnly /></RequireAuth>}>
        <Route path="/parcels" element={<ParcelHub />} />
        <Route path="/cs/parcels/:id/view-as/:seat" element={<ParcelDoor support />} />
      </Route>

      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        {/* Admin only: the API answers 404 to anyone not in ADMIN_EMAILS. */}
        <Route path="/admin/early-access" element={<AdminEarlyAccess />} />
        <Route
          path="/dashboard"
          element={
            <RequireRole role="contractor">
              <Overview />
            </RequireRole>
          }
        />
        <Route
          path="/properties"
          element={
            <RequireRole role="homeowner">
              <Properties />
            </RequireRole>
          }
        />
        <Route
          path="/jobs"
          element={
            <RequireRole role="contractor">
              <Jobs />
            </RequireRole>
          }
        />
        <Route
          path="/jobs/new"
          element={
            <RequireRole role="contractor">
              <NewJob />
            </RequireRole>
          }
        />
        <Route
          path="/jobs/:id"
          element={
            <RequireRole role="contractor">
              <JobDetail />
            </RequireRole>
          }
        />
        <Route
          path="/approvals"
          element={
            <RequireRole role="contractor">
              <RequireLevel min="manager">
                <Approvals />
              </RequireLevel>
            </RequireRole>
          }
        />
        <Route
          path="/wallet"
          element={
            <RequireRole role="contractor">
              <Wallet />
            </RequireRole>
          }
        />
        {/* Run 8 first-run interview: contractor admin only; shows once. */}
        <Route
          path="/office-setup"
          element={
            <RequireRole role="contractor">
              <RequireLevel min="admin">
                <OfficeSetup />
              </RequireLevel>
            </RequireRole>
          }
        />
        <Route
          path="/team"
          element={
            <RequireRole role="contractor">
              <RequireLevel min="admin">
                <Team />
              </RequireLevel>
            </RequireRole>
          }
        />
        {/* Run 10 brokerage sale files. Outside RequireRole on purpose: the
            listing agent (brokerage field) is redirected here from every
            role-gated office route, so wrapping it would loop. The page
            itself bounces non-brokerage accounts home. */}
        <Route path="/files" element={<Files />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      <Route path="/" element={<RoleHome />} />
      <Route path="*" element={<RoleHome />} />
      </Routes>
    </>
  );
}
